import React, { useState, useEffect, useRef, useCallback } from "react";

const PropertySearchAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search by property name, host, or location...",
  style = {},
  inputStyle = {},
  labelStyle = {},
  label = "",
  autoFocus = false,
  disabled = false,
  fetchProperties,
  selectedProperty = null,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  const performSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 1 || disabled) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      const results = await fetchProperties(searchTerm);
      setSuggestions(results || []);
      setShowSuggestions(results && results.length > 0);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  }, [fetchProperties, disabled]);

  // Debounce input changes
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, performSearch]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = (property) => {
    onSelect(property);
    onChange(formatPropertyDisplay(property));
    setShowSuggestions(false);
  };

  const formatPropertyDisplay = (property) => {
    if (!property) return '';
    const hostName = property.brand_name || property.owner_name || 'Host';
    const location = property.location || 
      (property.location_city && property.location_state 
        ? `${property.location_city}, ${property.location_state}`
        : property.location_city || property.location_state || '');
    return `${property.property_name} - ${hostName}${location ? ` (${location})` : ''}`;
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const defaultInputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
    color: '#0f084b',
    outline: 'none',
    ...inputStyle
  };

  const defaultLabelStyle = {
    display: 'block',
    marginBottom: '8px',
    color: '#333',
    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
    fontSize: '14px',
    ...labelStyle
  };

  // Update display value when selectedProperty changes
  useEffect(() => {
    if (selectedProperty && !value) {
      onChange(formatPropertyDisplay(selectedProperty));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', ...style }}>
      {label && <label style={defaultLabelStyle}>{label}</label>}
      
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0 && !disabled) {
            setShowSuggestions(true);
          }
        }}
        placeholder={placeholder}
        style={defaultInputStyle}
        autoFocus={autoFocus}
        disabled={disabled}
      />
      
      {loading && (
        <div style={{
          position: 'absolute',
          right: '12px',
          top: label ? '38px' : '10px',
          fontSize: '12px',
          color: '#999'
        }}>
          Searching...
        </div>
      )}
      
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid rgba(0,0,0,.12)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 1000,
          marginTop: '4px'
        }}>
          {suggestions.map((property, index) => {
            const hostName = property.brand_name || property.owner_name || 'Host';
            const location = property.location || 
              (property.location_city && property.location_state 
                ? `${property.location_city}, ${property.location_state}`
                : property.location_city || property.location_state || '');
            
            return (
              <div
                key={`${property.property_id}-${property.host_profile_id}`}
                onClick={() => handleSuggestionClick(property)}
                style={{
                  padding: '12px',
                  cursor: 'pointer',
                  backgroundColor: index === highlightedIndex ? '#f0f0f0' : 'white',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: '14px',
                  color: '#0f084b',
                  borderBottom: index < suggestions.length - 1 ? '1px solid rgba(0,0,0,.06)' : 'none',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                  {property.property_name}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {hostName}{location ? ` • ${location}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {showSuggestions && !loading && suggestions.length === 0 && value && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid rgba(0,0,0,.12)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '12px',
          zIndex: 1000,
          marginTop: '4px',
          fontSize: '14px',
          color: '#666',
          fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
        }}>
          No properties found
        </div>
      )}
    </div>
  );
};

export default PropertySearchAutocomplete;

